import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
    Dialog, DialogContent, DialogTitle,
} from '../ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Calendar } from '../ui/calendar';
import { disablePastDates, makeDisableBeforeDateChargement } from '../../lib/dateConstraints';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Switch } from '../ui/switch';
import { useEditLock } from '../../hooks/useEditLock';
import { LockBanner } from '../permissions/LockBanner';
import { ReadOnlyGuard } from '../permissions/ReadOnlyGuard';
import { usePermissions } from '../../contexts/PermissionsContext';
import { User, MapPin, Truck, CheckCircle2, Globe, Calendar as CalendarIcon, Loader2, ClipboardList, Warehouse, FileText, Sparkles, Banknote, ChevronDown, ChevronUp, Package, Wrench, Weight, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { RemarquesEditor } from './RemarquesEditor';

const API = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export function DevisFormDialog({ open, onOpenChange, inventaire, formule, formuleLabel, config, onSuccess, mode = 'create', devisData = null }) {
    const isPreview = mode === 'apercu';
    const isEdit = mode === 'modifier';
    const lockId = isEdit ? devisData?.id : null;
    const lock = useEditLock('devis', lockId, !!lockId && open);
    const { hasFlag } = usePermissions();

    // Flag "lock_devis_after_envoye" : une fois le devis envoyé/relancé/accepté/refusé/..., il est en lecture seule
    const LOCKED_STATUTS = new Set(['envoye', 'relance_1', 'relance_2', 'relance_3', 'interesse', 'accepte', 'refuse', 'sans_reponse']);
    const devisStatutLocked = !!(devisData && LOCKED_STATUTS.has(devisData.statut) && hasFlag('lock_devis_after_envoye'));
    // Aperçu pur OU devis verrouillé par le flag → force le mode lecture seule
    const forceReadOnly = isPreview || devisStatutLocked;

    if (!open) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-5xl w-full max-h-[90dvh] flex flex-col p-0 overflow-hidden"
                data-testid="devis-form-dialog"
                onPointerDownOutside={(e) => { if (!isPreview) e.preventDefault(); }}
                onEscapeKeyDown={(e) => { if (!isPreview) e.preventDefault(); }}
                hideCloseButton={!isPreview}
            >
                {lock.blocked && <LockBanner userName={lock.user_name} expiresAt={lock.expires_at} onClose={() => onOpenChange(false)} />}
                {/* En mode aperçu, le formulaire est déjà désactivé visuellement
                    (pointer-events-none + inputs disabled). On NE wrappe PAS dans
                    ReadOnlyGuard car son <fieldset disabled> désactive aussi le
                    bouton "Fermer" du header. */}
                {isPreview ? (
                    <DevisFormContent
                        inventaire={inventaire}
                        formule={formule}
                        formuleLabel={formuleLabel}
                        config={config}
                        onSuccess={onSuccess}
                        onCancel={() => onOpenChange(false)}
                        mode={mode}
                        devisData={devisData}
                    />
                ) : (
                    <ReadOnlyGuard module="devis" lockBlocked={lock.blocked} forceReadOnly={forceReadOnly}>
                        <DevisFormContent
                            inventaire={inventaire}
                            formule={formule}
                            formuleLabel={formuleLabel}
                            config={config}
                            onSuccess={onSuccess}
                            onCancel={() => onOpenChange(false)}
                            mode={mode}
                            devisData={devisData}
                        />
                    </ReadOnlyGuard>
                )}
            </DialogContent>
        </Dialog>
    );
}

function DevisFormContent({ inventaire, formule, formuleLabel, config, onSuccess, onCancel, mode = 'create', devisData = null }) {
    const isPreview = mode === 'apercu';
    const isEdit = mode === 'modifier';
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCorrecting, setIsCorrecting] = useState(false);
    const [isCorrectingDevis, setIsCorrectingDevis] = useState(false);
    const [showInfoPanel, setShowInfoPanel] = useState(false);
    const [infoData, setInfoData] = useState(null);
    const [loadingInfo, setLoadingInfo] = useState(false);
    const [initialSnapshot, setInitialSnapshot] = useState(null);

    const [formData, setFormData] = useState({
        nom: '', telephone: '', email: '', source: '', departement: '', remarques: '',
        commercial_id: '',
        type_inventaire: '',
        formule: formule || '',
        garde_meuble: null,
        garde_meuble_tarif: '',
        garde_meuble_duree: '',
        cave_garage: null,
        cave_garage_detail: '',
        remarques_inventaire: '',
        remarques_devis: '',
        prestations: [],
        prestations_additionnelles: [],
        volume_devis: '',
        distance: '',
        fournitures_active: null,
        fournitures: { cartons_standards: '', cartons_livres: '', cartons_verres: '', cartons_bouteilles: '', adhesif: '', bulle_grande: '', bulle_petite: '', autres: '' },
        adresse_depart: { date: null, adresse: '', etage: '', ascenseur: '', portage: '', autorisation_stationnement: null, monte_meuble: null },
        adresse_arrivee: { date: null, adresse: '', etage: '', ascenseur: '', portage: '', autorisation_stationnement: null, monte_meuble: null },
        bon_plan: { actif: null, montant: '', formule: '' }
    });

    const adresseDepartRef = useRef(null);
    const adresseArriveeRef = useRef(null);
    const acDepartRef = useRef(null);
    const acArriveeRef = useRef(null);

    useEffect(() => {
        const normalizeAddr = (a) => {
            if (!a) return { date: null, adresse: '', etage: '', ascenseur: '', portage: '', autorisation_stationnement: null, monte_meuble: null };
            let normalDate = null;
            if (a.date) {
                // Garde la date en YYYY-MM-DD sans conversion UTC (évite décalage fuseau)
                const s = String(a.date);
                normalDate = s.length >= 10 ? s.slice(0, 10) : s;
            }
            return {
                date: normalDate,
                adresse: a.adresse || '',
                etage: a.etage || '',
                ascenseur: a.ascenseur || '',
                portage: a.portage || '',
                autorisation_stationnement: a.autorisation_stationnement ?? null,
                monte_meuble: a.monte_meuble ?? null
            };
        };

        if ((isPreview || isEdit) && devisData) {
            const p = devisData.parametres || {};
            const inv = inventaire || {};
            const data = {
                client_nom: p.client_nom || devisData.client_nom || inv.client_nom || '',
                telephone: p.telephone || devisData.client_telephone || inv.telephone || '',
                email: p.email || devisData.client_email || inv.email || '',
                source: p.source || inv.source || '',
                departement: p.departement || inv.departement || '',
                remarques: p.remarques || inv.remarques || '',
                commercial_id: p.commercial_id || inv.commercial_id || '',
                type_inventaire: p.type_inventaire || inv.type_inventaire || '',
                formule: devisData.formule || '',
                garde_meuble: p.garde_meuble ?? inv.garde_meuble ?? null,
                garde_meuble_tarif: p.garde_meuble_tarif || inv.garde_meuble_tarif || '',
                garde_meuble_duree: p.garde_meuble_duree || inv.garde_meuble_duree || '',
                cave_garage: p.cave_garage ?? inv.cave_garage ?? null,
                cave_garage_detail: p.cave_garage_detail || inv.cave_garage_detail || '',
                remarques_inventaire: p.remarques_inventaire || inv.remarques_inventaire || '',
                remarques_devis: devisData.remarques_devis || p.remarques_devis || '',
                prestations: p.prestations?.length
                    ? p.prestations.map(pa => ({
                        nom: pa.nom || '',
                        // Migration douce : ancien champ prix_ht seul → calcule prix_ttc
                        prix_ttc: pa.prix_ttc !== undefined && pa.prix_ttc !== null && pa.prix_ttc !== ''
                            ? String(pa.prix_ttc)
                            : (pa.prix_ht ? String(Math.round(parseFloat(pa.prix_ht) * 1.2 * 100) / 100) : ''),
                    }))
                    : [{ nom: `Prestation déménagement - ${formuleLabel || formule || ''}`, prix_ttc: devisData.prix_final ? String(Math.round(devisData.prix_final * 100) / 100) : '' }],
                prestations_additionnelles: (p.prestations_additionnelles || []).map(pa => ({
                    nom: pa.nom || '',
                    // Migration douce : si l'ancien devis stocke seulement prix_ht, on calcule le TTC
                    prix_ttc: pa.prix_ttc !== undefined && pa.prix_ttc !== null && pa.prix_ttc !== ''
                        ? String(pa.prix_ttc)
                        : (pa.prix_ht ? String(Math.round(parseFloat(pa.prix_ht) * 1.2 * 100) / 100) : ''),
                })),
                volume_devis: p.volume_devis ?? inv.calcul_prix?.infos_base?.volume_devis ?? '',
                distance: p.distance ?? inv.calcul_prix?.infos_base?.distance ?? '',
                fournitures_active: p.fournitures_active ?? inv.calcul_prix?.fournitures_active ?? null,
                fournitures: { cartons_standards: '', cartons_livres: '', cartons_verres: '', cartons_bouteilles: '', adhesif: '', bulle_grande: '', bulle_petite: '', autres: '', ...(inv.calcul_prix?.fournitures || {}), ...(p.fournitures || {}) },
                adresse_depart: normalizeAddr(p.adresse_depart?.adresse ? p.adresse_depart : inv.adresse_depart),
                adresse_arrivee: normalizeAddr(p.adresse_arrivee?.adresse ? p.adresse_arrivee : inv.adresse_arrivee),
                bon_plan: devisData.bon_plan ? { actif: !!devisData.bon_plan.actif, montant: devisData.bon_plan.montant ?? '', formule: devisData.bon_plan.formule ?? '' } : { actif: null, montant: '', formule: '' }
            };
            setFormData(data);
            if (isEdit) setInitialSnapshot(JSON.stringify(data));
        } else if (inventaire) {
            setFormData({
                client_nom: inventaire.client_nom || '',
                telephone: inventaire.telephone || '',
                email: inventaire.email || '',
                source: inventaire.source || '',
                departement: inventaire.departement || '',
                remarques: inventaire.remarques || '',
                commercial_id: inventaire.commercial_id || '',
                type_inventaire: inventaire.type_inventaire || '',
                formule: formule || '',
                garde_meuble: inventaire.garde_meuble ?? null,
                garde_meuble_tarif: inventaire.garde_meuble_tarif || '',
                garde_meuble_duree: inventaire.garde_meuble_duree || '',
                cave_garage: inventaire.cave_garage ?? null,
                cave_garage_detail: inventaire.cave_garage_detail || '',
                remarques_inventaire: inventaire.remarques_inventaire || '',
                remarques_devis: inventaire.remarques_devis || '',
                prestations: [{ nom: `Prestation déménagement - ${formuleLabel || formule || ''}`, prix_ttc: '' }],
                prestations_additionnelles: [],
                volume_devis: inventaire.calcul_prix?.infos_base?.volume_devis || '',
                distance: inventaire.calcul_prix?.infos_base?.distance || '',
                fournitures_active: inventaire.calcul_prix?.fournitures_active ?? null,
                fournitures: { cartons_standards: '', cartons_livres: '', cartons_verres: '', cartons_bouteilles: '', adhesif: '', bulle_grande: '', bulle_petite: '', autres: '', ...(inventaire.calcul_prix?.fournitures || {}) },
                adresse_depart: normalizeAddr(inventaire.adresse_depart),
                adresse_arrivee: normalizeAddr(inventaire.adresse_arrivee),
                bon_plan: { actif: null, montant: '', formule: '' }
            });
        }
    }, [inventaire, formule, formuleLabel, isPreview, isEdit, devisData]);

    // Load Google Maps script if needed
    useEffect(() => {
        if (window.google?.maps?.places || !GOOGLE_MAPS_KEY) return;
        if (document.querySelector('script[src*="maps.googleapis.com"]')) return;
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places&language=fr`;
        script.async = true;
        document.head.appendChild(script);
    }, []);

    // Google Places autocomplete
    useEffect(() => {
        const trySetup = () => {
            if (!window.google?.maps?.places) return false;
            const setupAC = (ref, acRef, field) => {
                if (!ref.current || acRef.current) return;
                const ac = new window.google.maps.places.Autocomplete(ref.current, { componentRestrictions: { country: 'fr' }, fields: ['formatted_address'] });
                ac.addListener('place_changed', () => {
                    const place = ac.getPlace();
                    if (place.formatted_address) {
                        setFormData(prev => ({ ...prev, [field]: { ...prev[field], adresse: place.formatted_address } }));
                    }
                });
                acRef.current = ac;
            };
            setupAC(adresseDepartRef, acDepartRef, 'adresse_depart');
            setupAC(adresseArriveeRef, acArriveeRef, 'adresse_arrivee');
            return true;
        };
        if (trySetup()) return;
        const interval = setInterval(() => {
            if (trySetup()) clearInterval(interval);
        }, 500);
        return () => clearInterval(interval);
    }, []);

    const handleCreate = async () => {
        setIsSubmitting(true);
        try {
            // Pour les prestations (principales + additionnelles), l'utilisateur saisit en TTC.
            // On stocke prix_ttc (saisie) ET prix_ht (= prix_ttc / 1.2) pour rester compatible PDF.
            const principalesNormalisees = formData.prestations.map(p => {
                const ttc = parseFloat(p.prix_ttc) || 0;
                return {
                    nom: p.nom || '',
                    prix_ttc: ttc,
                    prix_ht: Math.round((ttc / 1.2) * 100) / 100,
                };
            });
            const additionnellesNormalisees = formData.prestations_additionnelles.map(p => {
                const ttc = parseFloat(p.prix_ttc) || 0;
                return {
                    nom: p.nom || '',
                    prix_ttc: ttc,
                    prix_ht: Math.round((ttc / 1.2) * 100) / 100,
                };
            });
            const totalHTCalc = principalesNormalisees.reduce((sum, p) => sum + p.prix_ht, 0)
                              + additionnellesNormalisees.reduce((sum, p) => sum + p.prix_ht, 0);
            const payload = {
                prix_final: totalHTCalc > 0 ? Math.round(totalHTCalc * 1.2 * 100) / 100 : null,
                remarques_devis: formData.remarques_devis,
                bon_plan: formData.bon_plan?.actif === true
                    ? {
                        actif: true,
                        montant: parseFloat(formData.bon_plan.montant) || 0,
                        formule: formData.bon_plan.formule || '',
                    }
                    : { actif: false, montant: 0, formule: '' },
                parametres: {
                    client_nom: formData.client_nom,
                    telephone: formData.telephone,
                    email: formData.email,
                    source: formData.source,
                    departement: formData.departement,
                    remarques: formData.remarques,
                    commercial_id: formData.commercial_id,
                    type_inventaire: formData.type_inventaire,
                    garde_meuble: formData.garde_meuble,
                    garde_meuble_tarif: formData.garde_meuble_tarif,
                    garde_meuble_duree: formData.garde_meuble_duree,
                    cave_garage: formData.cave_garage,
                    cave_garage_detail: formData.cave_garage_detail,
                    remarques_inventaire: formData.remarques_inventaire,
                    fournitures_active: formData.fournitures_active,
                    fournitures: formData.fournitures,
                    prestations: principalesNormalisees,
                    prestations_additionnelles: additionnellesNormalisees,
                    volume_devis: formData.volume_devis,
                    distance: formData.distance,
                    total_ht: totalHTCalc,
                    adresse_depart: formData.adresse_depart,
                    adresse_arrivee: formData.adresse_arrivee,
                },
            };

            if (isEdit && devisData?.id) {
                await axios.post(`${API}/api/devis/${devisData.id}/modifier`, payload, { withCredentials: true });
                toast.success('Devis modifié — nouvelle version créée');
            } else {
                await axios.post(`${API}/api/devis/create`, {
                    inventaire_id: inventaire.id,
                    formule: formData.formule,
                    ...payload,
                }, { withCredentials: true });
                toast.success('Devis créé avec succès');
            }
            onSuccess();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de l\'opération');
        } finally { setIsSubmitting(false); }
    };

    const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
    const updateAddress = (type, field, value) => setFormData(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));

    const updatePrestation = (index, value) => setFormData(prev => {
        const updated = [...prev.prestations];
        updated[index] = { ...updated[index], prix_ttc: value };
        return { ...prev, prestations: updated };
    });
    const addPrestationAdditionnelle = (nom) => setFormData(prev => ({
        ...prev, prestations_additionnelles: [...prev.prestations_additionnelles, { nom, prix_ttc: '' }]
    }));
    const updatePrestationAdditionnelle = (index, field, value) => setFormData(prev => {
        const updated = [...prev.prestations_additionnelles];
        updated[index] = { ...updated[index], [field]: value };
        return { ...prev, prestations_additionnelles: updated };
    });
    const removePrestationAdditionnelle = (index) => setFormData(prev => ({
        ...prev, prestations_additionnelles: prev.prestations_additionnelles.filter((_, i) => i !== index)
    }));

    const PRESTATIONS_PREDEFINIES = [
        'Garde meuble premier mois', 'Autorisation stationnement', 'Monte meuble', 'Déchetterie'
    ];

    // Total HT = somme des (prix_ttc / 1.2) des prestations principales et additionnelles
    const totalHT = formData.prestations.reduce((sum, p) => sum + ((parseFloat(p.prix_ttc) || 0) / 1.2), 0)
                  + formData.prestations_additionnelles.reduce((sum, p) => sum + ((parseFloat(p.prix_ttc) || 0) / 1.2), 0);
    const tva = Math.round(totalHT * 0.2 * 100) / 100;
    const totalTTC = Math.round(totalHT * 1.2 * 100) / 100;

    const correctText = async () => {
        if (!formData.remarques_inventaire?.trim()) return;
        setIsCorrecting(true);
        try {
            const res = await axios.post(`${API}/api/ai/correct-text`, { text: formData.remarques_inventaire }, { withCredentials: true });
            setFormData(prev => ({ ...prev, remarques_inventaire: res.data.corrected_text }));
            toast.success('Texte corrigé');
        } catch { toast.error('Erreur lors de la correction'); }
        finally { setIsCorrecting(false); }
    };

    const correctTextDevis = async () => {
        if (!formData.remarques_devis?.trim()) return;
        setIsCorrectingDevis(true);
        try {
            const res = await axios.post(`${API}/api/ai/correct-text`, { text: formData.remarques_devis }, { withCredentials: true });
            setFormData(prev => ({ ...prev, remarques_devis: res.data.corrected_text }));
            toast.success('Texte corrigé');
        } catch { toast.error('Erreur lors de la correction'); }
        finally { setIsCorrectingDevis(false); }
    };

    const loadInfoImportantes = async () => {
        if (showInfoPanel) { setShowInfoPanel(false); return; }
        if (infoData) { setShowInfoPanel(true); return; }
        setLoadingInfo(true);
        try {
            const { data } = await axios.get(`${API}/api/inventaires/${inventaire.id}/articles`, { withCredentials: true });
            const articles = data.articles || [];
            setInfoData({
                remarques_inventaire: formData.remarques_inventaire || inventaire.remarques_inventaire || '',
                demontage: articles.filter(a => a.demontage_remontage),
                objets_lourds: articles.filter(a => a.objet_lourd),
            });
            setShowInfoPanel(true);
        } catch {
            toast.error('Erreur lors du chargement');
        } finally { setLoadingInfo(false); }
    };

    const isSectionComplete = {
        infos: () => !!formData.client_nom && !!formData.telephone && !!formData.email,
        source: () => !!formData.source && !!formData.departement,
        adresseDepart: () => { const a = formData.adresse_depart; return a && !!a.date && !!a.adresse && !!a.etage && !!a.ascenseur && !!a.portage && a.autorisation_stationnement !== null && a.monte_meuble !== null; },
        adresseArrivee: () => { const a = formData.adresse_arrivee; return a && !!a.date && !!a.adresse && !!a.etage && !!a.ascenseur && !!a.portage && a.autorisation_stationnement !== null && a.monte_meuble !== null; },
        typeCommercial: () => !!formData.type_inventaire && !!formData.commercial_id
    };

    const gardeOk = formData.garde_meuble === false || (formData.garde_meuble === true && !!formData.garde_meuble_tarif && !!formData.garde_meuble_duree);
    const caveOk = formData.cave_garage === false || (formData.cave_garage === true && !!formData.cave_garage_detail);
    const bonPlanOk = formData.bon_plan?.actif === false || (formData.bon_plan?.actif === true && !!String(formData.bon_plan?.montant ?? '').trim() && !!String(formData.bon_plan?.formule ?? '').trim());
    const isFormComplete = isSectionComplete.infos() && isSectionComplete.adresseDepart() && isSectionComplete.adresseArrivee()
        && formData.garde_meuble !== null && gardeOk && formData.cave_garage !== null && caveOk && formData.bon_plan?.actif !== null && bonPlanOk && totalHT > 0;
    const hasChanges = isEdit && initialSnapshot ? JSON.stringify(formData) !== initialSnapshot : false;
    const canSubmit = isEdit ? hasChanges : isFormComplete;

    const SectionLine = ({ isComplete }) => (
        <div className={`w-1 rounded-full self-stretch shrink-0 ${isComplete ? 'bg-emerald-500' : 'bg-red-400'}`} />
    );
    const SectionCheck = ({ isComplete }) => (
        isComplete ? <CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> : null
    );

    const DatePickerField = ({ label, value, onChange, disabled = false, helperText, disabledFn }) => {
        // Parse YYYY-MM-DD en date locale pour éviter tout décalage fuseau
        const dateValue = value ? (() => {
            const s = String(value).slice(0, 10);
            const [y, m, d] = s.split('-').map(Number);
            return (y && m && d) ? new Date(y, m - 1, d) : null;
        })() : null;
        const toLocalISO = (d) => {
            if (!d) return null;
            const y = d.getFullYear();
            const mo = String(d.getMonth() + 1).padStart(2, '0');
            const da = String(d.getDate()).padStart(2, '0');
            return `${y}-${mo}-${da}`;
        };
        return (
            <div className="flex items-start gap-4">
                <Label className="text-slate-600 w-28 md:w-72 shrink-0 pt-2">{label}</Label>
                <div className="max-w-sm ml-auto w-full">
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className={`w-full justify-start text-left font-normal ${disabled ? 'cursor-not-allowed opacity-70' : ''}`}
                                disabled={disabled}
                                data-testid={`date-picker-${label}`}
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {dateValue ? format(dateValue, 'dd/MM/yyyy', { locale: fr }) : 'Sélectionner une date'}
                            </Button>
                        </PopoverTrigger>
                        {!disabled && (
                            <PopoverContent className="w-auto p-0 z-[60]" align="start">
                                <Calendar mode="single" selected={dateValue} onSelect={(d) => onChange(toLocalISO(d))} locale={fr} disabled={disabledFn || disablePastDates} />
                            </PopoverContent>
                        )}
                    </Popover>
                    {helperText && (
                        <p className="text-[11px] text-slate-400 mt-1 italic">{helperText}</p>
                    )}
                </div>
            </div>
        );
    };

    const renderAddressForm = (type, title, Icon, inputRef) => {
        const addr = formData[type];
        const isComplete = type === 'adresse_depart' ? isSectionComplete.adresseDepart() : isSectionComplete.adresseArrivee();
        return (
            <Card className="border-slate-200">
                <CardHeader className="pb-3 border-b border-slate-100">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                        <Icon size={16} className="text-[#2e7cd6]" />{title} <SectionCheck isComplete={isComplete} />
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                    <div className="flex gap-4">
                        <SectionLine isComplete={isComplete} />
                        <div className="flex-1 space-y-4">
                            <DatePickerField
                                label={type === 'adresse_depart' ? 'Date de chargement' : 'Date de déchargement'}
                                value={addr.date}
                                onChange={(val) => updateAddress(type, 'date', val)}
                                disabled={devisData?.statut === 'accepte'}
                                helperText={devisData?.statut === 'accepte' ? "La date d'un devis accepté se modifie uniquement depuis la fiche client (Programmation de la prestation)." : undefined}
                                disabledFn={
                                    type === 'adresse_arrivee'
                                        ? makeDisableBeforeDateChargement(formData.adresse_depart?.date)
                                        : disablePastDates
                                }
                            />
                            <div className="flex items-center gap-4">
                                <Label className="text-slate-600 w-28 md:w-72 shrink-0">Adresse</Label>
                                <Input ref={inputRef} className="max-w-sm ml-auto" value={addr.adresse} onChange={e => updateAddress(type, 'adresse', e.target.value)} placeholder="Saisir l'adresse complète" />
                            </div>
                            <div className="flex items-center gap-4">
                                <Label className="text-slate-600 w-28 md:w-72 shrink-0">Étage</Label>
                                <Input className="max-w-sm ml-auto" value={addr.etage} onChange={e => updateAddress(type, 'etage', e.target.value)} placeholder="Ex: 3ème" />
                            </div>
                            <div className="flex items-center gap-4">
                                <Label className="text-slate-600 w-28 md:w-72 shrink-0">Portage</Label>
                                <Input className="max-w-sm ml-auto" value={addr.portage} onChange={e => updateAddress(type, 'portage', e.target.value)} placeholder="Ex: 20m" />
                            </div>
                            <div className="flex items-center gap-4">
                                <Label className="text-slate-600 w-28 md:w-72 shrink-0">Ascenseur</Label>
                                <Select value={addr.ascenseur || undefined} onValueChange={v => updateAddress(type, 'ascenseur', v)}>
                                    <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                    <SelectContent>{config?.ascenseur_options?.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center gap-4">
                                <Label className="text-slate-600 w-28 md:w-72 shrink-0">Autorisation stationnement</Label>
                                <Select value={addr.autorisation_stationnement === true ? 'oui' : addr.autorisation_stationnement === false ? 'non' : ''} onValueChange={v => updateAddress(type, 'autorisation_stationnement', v === 'oui')}>
                                    <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                    <SelectContent><SelectItem value="oui">Oui</SelectItem><SelectItem value="non">Non</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center gap-4">
                                <Label className="text-slate-600 w-28 md:w-72 shrink-0">Monte-meuble</Label>
                                <Select value={addr.monte_meuble === true ? 'oui' : addr.monte_meuble === false ? 'non' : ''} onValueChange={v => updateAddress(type, 'monte_meuble', v === 'oui')}>
                                    <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                    <SelectContent><SelectItem value="oui">Oui</SelectItem><SelectItem value="non">Non</SelectItem></SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    if (!config && !isPreview) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
            </div>
        );
    }

    return (
        <>
            {/* Sticky Header */}
            <div className="sticky top-0 z-50 bg-white border-b px-4 md:px-6 py-3 md:py-4">
                <div className="flex items-center justify-between gap-2">
                    <div>
                        <DialogTitle className="text-base md:text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {isPreview ? `Aperçu devis ${devisData?.numero_devis || ''} — ${formuleLabel}`
                                : isEdit ? `Modifier devis ${devisData?.numero_devis || ''} — ${formuleLabel}`
                                : `Créer un devis — ${formuleLabel}`}
                        </DialogTitle>
                        <p className="text-sm text-slate-500">
                            {isPreview ? 'Consultation des informations du devis'
                                : isEdit ? 'Modifiez les informations puis enregistrez — une nouvelle version sera créée'
                                : 'Remplissez les informations du devis'}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {isPreview ? (
                            <Button type="button" variant="outline" onClick={onCancel} data-testid="devis-form-close-btn">
                                Fermer
                            </Button>
                        ) : (
                            <>
                                <Button type="button" variant="outline" onClick={onCancel} data-testid="devis-form-cancel-btn">
                                    Annuler
                                </Button>
                                <Button onClick={handleCreate} disabled={isSubmitting || !canSubmit} className={`text-white ${canSubmit ? 'bg-[#2e7cd6] hover:bg-[#2568b8]' : 'bg-[#2e7cd6]/40 cursor-not-allowed'}`} data-testid="devis-form-submit-btn">
                                    {isSubmitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                                    {isEdit ? 'Enregistrer' : 'Créer'}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Scrollable Form */}
            <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-4">
                <div className={`space-y-6 ${isPreview ? 'pointer-events-none' : ''}`}>

                {/* 1. Informations générales */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <User size={16} className="text-[#2e7cd6]" />1. Informations générales <SectionCheck isComplete={isSectionComplete.infos()} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={isSectionComplete.infos()} />
                            <div className="flex-1 space-y-4">
                                <div className="flex items-center gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Nom *</Label>
                                    <Input className="max-w-sm ml-auto" value={formData.client_nom} onChange={e => updateField('client_nom', e.target.value)} placeholder="Nom du client" />
                                </div>
                                <div className="flex items-center gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Téléphone</Label>
                                    <Input className="max-w-sm ml-auto" value={formData.telephone} onChange={e => updateField('telephone', e.target.value)} placeholder="06 00 00 00 00" />
                                </div>
                                <div className="flex items-center gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Email</Label>
                                    <Input className="max-w-sm ml-auto" type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} placeholder="email@exemple.fr" />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 2. Adresse de départ */}
                {renderAddressForm('adresse_depart', "2. Adresse de départ", MapPin, adresseDepartRef)}

                {/* 3. Adresse d'arrivée */}
                {renderAddressForm('adresse_arrivee', "3. Adresse d'arrivée", Truck, adresseArriveeRef)}

                {/* 4. Garde-meuble */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <Warehouse size={16} className="text-[#2e7cd6]" />4. Garde-meuble <SectionCheck isComplete={formData.garde_meuble === false || (formData.garde_meuble === true && !!formData.garde_meuble_tarif && !!formData.garde_meuble_duree)} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={formData.garde_meuble === false || (formData.garde_meuble === true && !!formData.garde_meuble_tarif && !!formData.garde_meuble_duree)} />
                            <div className="flex-1 space-y-4">
                                <div className="flex items-center gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Garde-meuble</Label>
                                    <Select value={formData.garde_meuble === true ? 'oui' : formData.garde_meuble === false ? 'non' : ''} onValueChange={v => {
                                        if (v !== 'oui' && v !== 'non') return; // Radix Select émet '' au montage → ne pas écraser la valeur
                                        const val = v === 'oui';
                                        setFormData(prev => ({ ...prev, garde_meuble: val, garde_meuble_tarif: val ? prev.garde_meuble_tarif : '', garde_meuble_duree: val ? prev.garde_meuble_duree : '' }));
                                    }}>
                                        <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                        <SelectContent><SelectItem value="oui">Oui</SelectItem><SelectItem value="non">Non</SelectItem></SelectContent>
                                    </Select>
                                </div>
                                {formData.garde_meuble === true && (
                                    <>
                                        <div className="flex items-center gap-4">
                                            <Label className="text-slate-600 w-28 md:w-72 shrink-0">Mensualité (tarif)</Label>
                                            <Input className="max-w-sm ml-auto" value={formData.garde_meuble_tarif} onChange={e => updateField('garde_meuble_tarif', e.target.value)} placeholder="Ex: 150€/mois" />
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <Label className="text-slate-600 w-28 md:w-72 shrink-0">Durée</Label>
                                            <Input className="max-w-sm ml-auto" value={formData.garde_meuble_duree} onChange={e => updateField('garde_meuble_duree', e.target.value)} placeholder="Ex: 3 mois" />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 5. Fournitures */}
                {(() => {
                    const REQUIRED_FOURNITURES = ['cartons_standards', 'cartons_livres', 'cartons_verres', 'cartons_bouteilles', 'adhesif', 'bulle_grande', 'bulle_petite'];
                    const fournituresComplete = formData.fournitures_active === false
                        || (formData.fournitures_active === true
                            && REQUIRED_FOURNITURES.every(k => {
                                const v = formData.fournitures?.[k];
                                return v !== '' && v !== null && v !== undefined;
                            }));
                    return (
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700 flex-wrap">
                            <Warehouse size={16} className="text-[#2e7cd6]" />5. Fournitures
                            <SectionCheck isComplete={fournituresComplete} />
                            <span className="text-xs font-medium text-red-600 ml-1">
                                Choisis soit la petite bulle, soit la grande — pas les deux.
                            </span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={fournituresComplete} />
                            <div className="flex-1 space-y-4">
                                <div className="flex items-center gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Fournitures</Label>
                                    <Select value={formData.fournitures_active === true ? 'oui' : formData.fournitures_active === false ? 'non' : ''} onValueChange={v => {
                                        if (v !== 'oui' && v !== 'non') return; // Radix Select émet '' au montage → ne pas écraser la valeur
                                        const val = v === 'oui';
                                        setFormData(prev => ({ ...prev, fournitures_active: val }));
                                    }}>
                                        <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                        <SelectContent><SelectItem value="oui">Oui</SelectItem><SelectItem value="non">Non</SelectItem></SelectContent>
                                    </Select>
                                </div>
                                {formData.fournitures_active === true && (
                                    <>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {[
                                                { key: 'cartons_standards', label: 'Cartons standards' },
                                                { key: 'cartons_livres', label: 'Cartons livres' },
                                                { key: 'cartons_verres', label: 'Cartons verres (100 verres)' },
                                                { key: 'cartons_bouteilles', label: 'Cartons bouteilles' },
                                            ].map(f => (
                                                <div key={f.key}>
                                                    <Label className="text-xs text-slate-500">{f.label}</Label>
                                                    <Input type="number" min="0" value={formData.fournitures[f.key]} onChange={e => setFormData(p => ({ ...p, fournitures: { ...p.fournitures, [f.key]: e.target.value } }))} className="mt-1 h-9 text-sm" />
                                                </div>
                                            ))}
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {[
                                                { key: 'adhesif', label: 'Adhésif' },
                                                { key: 'bulle_grande', label: 'Bulle (100m x 50cm)' },
                                                { key: 'bulle_petite', label: 'Bulle (10m x 1m)' },
                                            ].map(f => (
                                                <div key={f.key}>
                                                    <Label className="text-xs text-slate-500">{f.label}</Label>
                                                    <Input type="number" min="0" value={formData.fournitures[f.key]} onChange={e => setFormData(p => ({ ...p, fournitures: { ...p.fournitures, [f.key]: e.target.value } }))} className="mt-1 h-9 text-sm" />
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
                    );
                })()}

                {/* 6. Cave / Garage */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <Warehouse size={16} className="text-[#2e7cd6]" />6. Cave / Garage <SectionCheck isComplete={formData.cave_garage === false || (formData.cave_garage === true && !!formData.cave_garage_detail)} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={formData.cave_garage === false || (formData.cave_garage === true && !!formData.cave_garage_detail)} />
                            <div className="flex-1 space-y-4">
                                <div className="flex items-center gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Cave / Garage</Label>
                                    <Select value={formData.cave_garage === true ? 'oui' : formData.cave_garage === false ? 'non' : ''} onValueChange={v => {
                                        if (v !== 'oui' && v !== 'non') return; // Radix Select émet '' au montage → ne pas écraser la valeur
                                        const val = v === 'oui';
                                        setFormData(prev => ({ ...prev, cave_garage: val, cave_garage_detail: val ? prev.cave_garage_detail : '' }));
                                    }}>
                                        <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                        <SelectContent><SelectItem value="oui">Oui</SelectItem><SelectItem value="non">Non</SelectItem></SelectContent>
                                    </Select>
                                </div>
                                {formData.cave_garage === true && (
                                    <div className="flex items-center gap-4">
                                        <Label className="text-slate-600 w-28 md:w-72 shrink-0">Détail</Label>
                                        <Input className="max-w-sm ml-auto" value={formData.cave_garage_detail} onChange={e => updateField('cave_garage_detail', e.target.value)} placeholder="Ex: au -1 volume 5 m³" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 7. Remarques devis (formule) */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <FileText size={16} className="text-[#2e7cd6]" />7. Remarques devis ({formuleLabel}) <SectionCheck isComplete={!!formData.remarques_devis} />
                            <Button type="button" variant="outline" size="sm" onClick={correctTextDevis} disabled={isCorrectingDevis || !formData.remarques_devis?.trim()} className="gap-2 ml-auto border-[#2e7cd6] text-[#2e7cd6] hover:bg-[#2e7cd6]/10">
                                {isCorrectingDevis ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                                {isCorrectingDevis ? 'Correction...' : 'Corriger'}
                            </Button>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={!!formData.remarques_devis} />
                            <div className="flex-1 space-y-4">
                                <RemarquesEditor
                                    value={formData.remarques_devis}
                                    onChange={(v) => updateField('remarques_devis', v)}
                                    placeholder="Notes et remarques pour le devis..."
                                    dataTestId="remarques-devis-editor"
                                />

                                {/* Bouton afficher/masquer infos - style identique au calculateur de volume */}
                                <button
                                    type="button"
                                    onClick={loadInfoImportantes}
                                    disabled={loadingInfo}
                                    className="w-full flex items-center justify-center gap-2 py-2 text-xs font-medium text-slate-500 hover:text-[#2e7cd6] transition-colors rounded-lg hover:bg-slate-50 border border-dashed border-slate-300"
                                    data-testid="consulter-infos-btn"
                                >
                                    {loadingInfo ? <Loader2 size={14} className="animate-spin" /> : showInfoPanel ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                    {showInfoPanel ? 'Masquer les informations importantes' : 'Consulter les informations importantes'}
                                </button>

                                {/* Panel infos importantes - texte simple pour faciliter le copier */}
                                {showInfoPanel && infoData && (
                                    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 space-y-3 text-sm text-slate-700 select-text" data-testid="info-panel">
                                        <div>
                                            <span className="font-semibold text-slate-800">Remarques inventaire :</span>
                                            <p className="whitespace-pre-wrap mt-1">{infoData.remarques_inventaire || <span className="text-slate-400 italic">Aucune remarque</span>}</p>
                                        </div>
                                        <div>
                                            <span className="font-semibold text-slate-800">Démontage :</span>
                                            <p className="mt-1">
                                                {infoData.demontage.length > 0
                                                    ? infoData.demontage.map(a => `${a.quantite} ${a.nom}`).join(', ')
                                                    : <span className="text-slate-400 italic">Aucun article à démonter</span>}
                                            </p>
                                        </div>
                                        <div>
                                            <span className="font-semibold text-slate-800">Objets lourds :</span>
                                            <p className="mt-1">
                                                {infoData.objets_lourds.length > 0
                                                    ? infoData.objets_lourds.map(a => `${a.quantite} ${a.nom}`).join(', ')
                                                    : <span className="text-slate-400 italic">Aucun objet lourd</span>}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 8. Volume devis et distance */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <Weight size={16} className="text-[#2e7cd6]" />8. Volume devis et distance <SectionCheck isComplete={!!formData.volume_devis && !!formData.distance} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={!!formData.volume_devis && !!formData.distance} />
                            <div className="flex-1 grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-semibold text-slate-600">Volume devis (m³)</Label>
                                    <Input
                                        type="number" min="0" step="any"
                                        value={formData.volume_devis}
                                        onChange={e => updateField('volume_devis', e.target.value)}
                                        placeholder="Ex : 25"
                                        className="mt-1 h-9"
                                        disabled={isPreview}
                                        data-testid="devis-volume-devis"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-slate-600">Distance (km)</Label>
                                    <Input
                                        type="number" min="0" step="any"
                                        value={formData.distance}
                                        onChange={e => updateField('distance', e.target.value)}
                                        placeholder="Ex : 120"
                                        className="mt-1 h-9"
                                        disabled={isPreview}
                                        data-testid="devis-distance"
                                    />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 9. Prix final */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <Banknote size={16} className="text-[#2e7cd6]" />9. Prix final <SectionCheck isComplete={totalHT > 0} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={totalHT > 0} />
                            <div className="flex-1 space-y-3">

                                {/* Prestation principale */}
                                {formData.prestations.map((p, i) => (
                                    <div key={`main-${i}`} className="flex items-center gap-3" data-testid={`prestation-main-${i}`}>
                                        <span className="flex-1 text-sm font-medium text-slate-700 truncate">{p.nom}</span>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                className="w-28 h-9 text-sm text-right"
                                                value={p.prix_ttc ?? ''}
                                                onChange={e => updatePrestation(i, e.target.value)}
                                                placeholder="0.00"
                                                data-testid={`prestation-main-prix-${i}`}
                                            />
                                            <span className="text-xs text-slate-500 w-10">€ TTC</span>
                                        </div>
                                    </div>
                                ))}

                                {/* Prestations additionnelles */}
                                {formData.prestations_additionnelles.map((p, i) => (
                                    <div key={`add-${i}`} className="flex items-center gap-3 pl-4 border-l-2 border-slate-200" data-testid={`prestation-add-${i}`}>
                                        <Input
                                            className="flex-1 h-9 text-sm"
                                            value={p.nom}
                                            onChange={e => updatePrestationAdditionnelle(i, 'nom', e.target.value)}
                                            placeholder="Nom de la prestation"
                                            data-testid={`prestation-add-nom-${i}`}
                                        />
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                className="w-28 h-9 text-sm text-right"
                                                value={p.prix_ttc ?? ''}
                                                onChange={e => updatePrestationAdditionnelle(i, 'prix_ttc', e.target.value)}
                                                placeholder="0.00"
                                                data-testid={`prestation-add-prix-${i}`}
                                            />
                                            <span className="text-xs text-slate-500 w-10">€ TTC</span>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 text-slate-400 hover:text-red-500 shrink-0"
                                            onClick={() => removePrestationAdditionnelle(i)}
                                            data-testid={`prestation-add-delete-${i}`}
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    </div>
                                ))}

                                {/* Bouton ajouter prestation */}
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button
                                            type="button"
                                            className="w-full flex items-center justify-center gap-2 py-2 text-xs font-medium text-slate-500 hover:text-[#2e7cd6] transition-colors rounded-lg hover:bg-slate-50 border border-dashed border-slate-300"
                                            data-testid="add-prestation-btn"
                                        >
                                            <Plus size={14} />
                                            Prestation additionnelle
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-56 p-1" align="start">
                                        <div className="space-y-0.5">
                                            {PRESTATIONS_PREDEFINIES.map(nom => (
                                                <button
                                                    key={nom}
                                                    type="button"
                                                    className="w-full text-left text-sm px-3 py-2 rounded hover:bg-slate-100 text-slate-700 transition-colors"
                                                    onClick={() => addPrestationAdditionnelle(nom)}
                                                    data-testid={`add-prestation-${nom.toLowerCase().replace(/\s+/g, '-')}`}
                                                >
                                                    {nom}
                                                </button>
                                            ))}
                                            <div className="border-t border-slate-100 my-1" />
                                            <button
                                                type="button"
                                                className="w-full text-left text-sm px-3 py-2 rounded hover:bg-slate-100 text-[#2e7cd6] font-medium transition-colors"
                                                onClick={() => addPrestationAdditionnelle('')}
                                                data-testid="add-prestation-custom"
                                            >
                                                + Personnalisé
                                            </button>
                                        </div>
                                    </PopoverContent>
                                </Popover>

                                {/* Totaux */}
                                <div className="border-t border-slate-200 pt-3 mt-2 space-y-1.5">
                                    <div className="flex justify-between text-sm text-slate-600">
                                        <span>Total HT</span>
                                        <span data-testid="total-ht">{totalHT.toFixed(2)} €</span>
                                    </div>
                                    <div className="flex justify-between text-sm text-slate-600">
                                        <span>TVA (20%)</span>
                                        <span data-testid="total-tva">{tva.toFixed(2)} €</span>
                                    </div>
                                    <div className="flex justify-between text-base font-bold text-slate-900 pt-1 border-t border-slate-200">
                                        <span>Total TTC</span>
                                        <span data-testid="total-ttc">{totalTTC.toFixed(2)} €</span>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 10. Bon plan */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <span className="inline-flex items-center justify-center w-4 h-4 text-[#2e7cd6]">🎁</span>
                            10. Bon plan (obligatoire)
                            <SectionCheck isComplete={bonPlanOk && formData.bon_plan?.actif !== null} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={bonPlanOk && formData.bon_plan?.actif !== null} />
                            <div className="flex-1 space-y-4">
                                <div className="flex items-center gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Afficher un « Bon plan » dans le PDF ?</Label>
                                    <Select
                                        value={formData.bon_plan?.actif === true ? 'oui' : formData.bon_plan?.actif === false ? 'non' : ''}
                                        onValueChange={v => {
                                            if (v !== 'oui' && v !== 'non') return; // Radix Select émet '' au montage → ne pas écraser la valeur
                                            const val = v === 'oui';
                                            setFormData(prev => ({
                                                ...prev,
                                                bon_plan: {
                                                    actif: val,
                                                    montant: val ? prev.bon_plan?.montant ?? '' : '',
                                                    formule: val ? prev.bon_plan?.formule ?? '' : '',
                                                },
                                            }));
                                        }}
                                    >
                                        <SelectTrigger className="max-w-sm ml-auto" data-testid="bon-plan-actif-select">
                                            <SelectValue placeholder="Sélectionner" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="oui">Oui</SelectItem>
                                            <SelectItem value="non">Non</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {formData.bon_plan?.actif === true && (
                                    <>
                                        <div className="flex items-center gap-4">
                                            <Label className="text-slate-600 w-28 md:w-72 shrink-0">Montant supplémentaire (€)</Label>
                                            <Input
                                                type="number" min="0" step="any"
                                                className="max-w-sm ml-auto"
                                                value={formData.bon_plan?.montant ?? ''}
                                                onChange={e => updateField('bon_plan', { ...(formData.bon_plan || {}), montant: e.target.value })}
                                                placeholder="Ex : 50"
                                                data-testid="bon-plan-montant"
                                            />
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <Label className="text-slate-600 w-28 md:w-72 shrink-0">Formule à proposer</Label>
                                            <Input
                                                type="text"
                                                className="max-w-sm ml-auto"
                                                value={formData.bon_plan?.formule || ''}
                                                onChange={e => updateField('bon_plan', { ...(formData.bon_plan || {}), formule: e.target.value })}
                                                placeholder="Ex : standard"
                                                data-testid="bon-plan-formule"
                                            />
                                        </div>
                                        <div className="rounded-lg border-2 border-blue-400 bg-blue-50 p-3 text-center text-sm text-slate-800">
                                            <span className="inline-flex items-center gap-1 font-bold text-blue-700 mb-1">
                                                <span>🎁</span> BON PLAN
                                            </span>
                                            <p className="text-slate-700">
                                                Pour seulement <span className="font-bold text-blue-700">{formData.bon_plan?.montant || '__'}€</span> de plus, vous pouvez passer sur la formule <span className="font-bold text-blue-700">{formData.bon_plan?.formule || '__'}</span>.
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                </div>
            </div>
        </>
    );
}
